import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Primitives";

/** Link an existing ticket as a CHILD of `ticket` (the prop, the parent).
 *
 *  `POST /api/tickets/{ticket_id}/parent` -- the PATH names the CHILD, the
 *  BODY names the parent (`ticket_links.py`'s own docstring, worded the same
 *  way `MergeTicketDialog.jsx` words its identical PATH-names-the-loser
 *  hazard: reversing it links the wrong ticket, and a test suite that
 *  reverses the same way stays green regardless). So the candidate the
 *  operator picks below -- the one becoming a CHILD -- is what goes in the
 *  URL path; `ticket.id` (this page's own ticket, becoming the PARENT) is
 *  what goes in the request body as `parent_id`. This component never lets
 *  the two swap: `ticket` is a fixed prop, only the local `candidate`
 *  selection moves.
 *
 *  Search runs server-side (`GET /tickets?q=...`), same debounced pattern as
 *  MergeTicketDialog.jsx, for the same reason: a one-shot page fetch would
 *  only ever reach the newest ~50-200 of however many thousand tickets a
 *  tenant holds.
 *
 *  A candidate that already has a parent, or already has children of its
 *  own, is left in the list rather than filtered out client-side -- the
 *  server's own one-level-nesting refusal (409, naming why) is the real
 *  gate, and hiding candidates the caller cannot fully evaluate risk hiding
 *  the exact ticket they meant to pick. */
export default function AddChildDialog({ ticket, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [clientNames, setClientNames] = useState({});
  const [loadingList, setLoadingList] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClientNames(Object.fromEntries(
      asArray(r.data).map((x) => [x.id, x.name])))).catch(() => setClientNames({}));
  }, []);

  useEffect(() => {
    setLoadingList(true);
    const params = { limit: 25 };
    if (q.trim()) params.q = q.trim();
    const h = setTimeout(() => {
      api.get("/tickets", { params })
        .then((r) => {
          const items = r.data && typeof r.data === "object" && r.data.items
            ? r.data.items : asArray(r.data);
          setCandidates(items.filter((x) => x.id !== ticket.id && x.parent_id !== ticket.id));
        })
        .catch(() => setCandidates([]))
        .finally(() => setLoadingList(false));
    }, q.trim() ? 300 : 0);
    return () => clearTimeout(h);
  }, [q, ticket.id]);

  const confirmLink = async () => {
    if (!candidate) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/tickets/${candidate.id}/parent`, { parent_id: ticket.id });
      toast.success(`#${candidate.number} is now a child of #${ticket.number}`);
      onLinked?.(candidate);
    } catch (e) {
      // formatApiError, never a raw e?.response?.data?.detail -- the 409s
      // this route can return (different client, already nested one level,
      // a cycle, either ticket merged) are strings today, but a bare
      // client-error shape must never be assumed stable enough to skip the
      // normaliser that exists for exactly this.
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent data-testid="add-child-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Add child to #{ticket.number}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {candidate
              ? "Confirm which ticket becomes a child of this one."
              : "Pick the ticket by number or title. One level of nesting only -- a ticket that is already a parent or already a child cannot be linked."}
          </DialogDescription>
        </DialogHeader>

        {!candidate && (
          <div className="space-y-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by # or title…" data-testid="add-child-search-input"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
            <div className="max-h-72 overflow-y-auto border border-zinc-900 rounded divide-y divide-zinc-900">
              {loadingList && <div className="px-3 py-4 text-sm text-zinc-500">Loading tickets…</div>}
              {!loadingList && candidates.length === 0 && (
                <div className="px-3 py-4 text-sm text-zinc-500">No tickets match.</div>
              )}
              {!loadingList && candidates.map((c) => (
                <button key={c.id} onClick={() => setCandidate(c)} data-testid={`add-child-candidate-${c.id}`}
                  aria-label={`Ticket #${c.number}, ${c.title}, ${clientNames[c.client_id] || "unknown client"}, status ${c.status}`}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900/60 flex items-center gap-2">
                  <span className="font-mono text-xs text-zinc-500 shrink-0" aria-hidden="true">#{c.number}</span>
                  <span className="text-sm text-zinc-200 truncate flex-1" aria-hidden="true">{c.title}</span>
                  <span className="text-[11px] text-zinc-500 shrink-0" aria-hidden="true">{clientNames[c.client_id] || "—"}</span>
                  <StatusBadge status={c.status} />
                </button>
              ))}
            </div>
          </div>
        )}

        {candidate && (
          <div className="space-y-3">
            <div className="rounded border border-zinc-900 bg-zinc-900/40 p-3 text-sm leading-relaxed text-zinc-300" data-testid="add-child-confirm-text">
              Make <span className="font-mono text-zinc-100">#{candidate.number}</span> — "{candidate.title}"
              {" "}a child of{" "}
              <span className="font-mono text-zinc-100">#{ticket.number}</span> — "{ticket.title}"?
            </div>
            {err && (
              <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="add-child-error">
                {err}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {candidate ? (
            <>
              <button onClick={() => { setCandidate(null); setErr(""); }} disabled={busy}
                className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Back</button>
              <button onClick={onClose} disabled={busy}
                className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
              <button onClick={confirmLink} disabled={busy} data-testid="add-child-confirm-btn"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
                {busy ? "Linking…" : `Link as child`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
